import {
  EntityLinks,
  GoalTreeIndex,
  ID,
  InheritColors,
  LifeArea,
} from '@griply/types';

import { getDirectGoalLifeAreaIds } from './get-goal-life-area';
import { getGoalParentIds } from './goals/get-goal-parents';

// The stored strings outlive the options: a goal whose links are never edited keeps its value
// forever, so both stay readable even after the pickers stop offering them.
export const normalizeInheritedColor = (
  color?: string | null,
): string | null => (!color || color === InheritColors.Goal ? null : color);

// Both sentinels and the empty value resolve through the goal's links instead of standing alone.
export const isResolvedColor = (color?: string | null): boolean =>
  !!color && color !== InheritColors.Goal && color !== InheritColors.LifeArea;

export type GetLinkedItemColorOptions = {
  goals: {
    id: ID;
    ancestorIds?: ID[] | null;
    parentLinksMigrated?: boolean | null;
    lifeAreaId?: ID | null;
    lifeAreaLinksMigrated?: boolean | null;
    color?: string | null;
  }[];
  lifeAreas: LifeArea[];
  // Without them a migrated goal resolves no parent and loses its inherited colour.
  links?: EntityLinks;
  goalsById?: Map<ID, GetLinkedItemColorOptions['goals'][number]>;
  usedIds?: Set<ID>;
  index?: GoalTreeIndex;
};

const resolveLinkedItemColor = (
  linkedItemId: ID,
  options: GetLinkedItemColorOptions,
): string | null => {
  const { lifeAreas, goals, goalsById, usedIds, index } = options;
  const links = options.links ?? index?.links;
  const _usedIds = usedIds ?? new Set();
  // to prevent loops we cancel out when revisiting a used ID
  if (_usedIds.has(linkedItemId)) {
    return null;
  }
  _usedIds.add(linkedItemId);

  const lookup = index?.goalById ?? goalsById;
  const goal = lookup
    ? lookup.get(linkedItemId)
    : goals.find(({ id }) => id === linkedItemId);
  const lifeArea = goal
    ? undefined
    : lifeAreas.find(({ id }) => id === linkedItemId);

  // Without `links` a caller cannot resolve relationship parents at all, so the frozen chain stays
  // the best available answer — going through getGoalParentIds there would strip a migrated goal's
  // inherited colour instead of improving it.
  const ancestorIds = goal?.ancestorIds ?? [];
  const parentId = links
    ? getGoalParentIds(
        {
          id: goal?.id ?? linkedItemId,
          ancestorIds: goal?.ancestorIds ?? null,
          parentLinksMigrated: goal?.parentLinksMigrated,
        },
        { links },
      )[0]
    : ancestorIds[ancestorIds.length - 1];
  // `InheritColors.Goal` took every branch that no colour at all takes, so it IS no colour.
  // `InheritColors.LifeArea` is the one that differs: it skips the parent.
  const ownColor = normalizeInheritedColor(goal?.color);

  if (ownColor === null && parentId) {
    return getLinkedItemColor(parentId, {
      lifeAreas,
      goals,
      links,
      goalsById,
      usedIds: _usedIds,
      index,
    });
  }

  if (ownColor === null || ownColor === InheritColors.LifeArea) {
    const goalLifeAreaId = goal
      ? getDirectGoalLifeAreaIds(goal, links)[0]
      : undefined;

    if (goalLifeAreaId) {
      return lifeAreas.find(({ id }) => id === goalLifeAreaId)?.color ?? null;
    }

    return lifeArea?.color ?? null;
  }

  return ownColor;
};

export const getLinkedItemColor = (
  linkedItemId: ID,
  options: GetLinkedItemColorOptions,
): string | null => {
  const { index, usedIds } = options;

  // A mid-recursion result depends on the entry point's usedIds, so only top-level calls cache.
  if (!index || usedIds) {
    return resolveLinkedItemColor(linkedItemId, options);
  }

  const cached = index.colorByLinkedId.get(linkedItemId);
  if (cached !== undefined) {
    return cached;
  }

  const color = resolveLinkedItemColor(linkedItemId, options);
  index.colorByLinkedId.set(linkedItemId, color);
  return color;
};
